<template>
    <Teleport to="body">
      <div v-if="show" class="modal fixed inset-0 bg-gray-800 bg-opacity-75 overflow-y-auto h-full w-full flex items-start justify-center z-[105] py-4" @click.self="closeModal">
        <div class="modal-content relative mx-auto p-6 border-0 w-full max-w-lg shadow-2xl rounded-lg bg-white dark:bg-gray-800 max-h-[90vh] overflow-y-auto styled-scrollbar my-4">
          <button class="close-modal absolute top-0 right-0 mt-4 mr-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-3xl font-light" @click="closeModal">&times;</button>
          <h3 class="horse-select-title text-2xl font-semibold mb-6 text-center">Valitse hevonen</h3>
  
          <div class="money-available-info mb-4 p-3 bg-teal-900/30 border border-teal-500/30 rounded-lg">
            <div class="flex justify-between items-center">
              <span class="text-teal-300 font-medium">Käytettävissä:</span>
              <span class="text-teal-300 font-bold text-lg">{{ (playerMoney + changingHorsePrice).toLocaleString() }} €</span>
            </div>
          </div>
          
          <div class="controls-grid mb-4 bg-gray-50 dark:bg-gray-900/60 rounded-lg px-4 py-3 form-controls-container">
            <div class="controls-row flex flex-col md:flex-row md:items-end md:gap-6 w-full">
              <div class="flex-1">
                <label for="horseSearch" class="block text-sm font-semibold mb-1">Hae nimellä:</label>
                <input
                  id="horseSearch"
                  v-model="searchTerm"
                  type="text"
                  placeholder="Hevosen nimi..."
                  class="custom-input w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition duration-150 ease-in-out"
                />
              </div>
              <div class="flex-1 mt-4 md:mt-0">
                <label for="horseSortOrder" class="block text-sm font-medium mb-1">Lajittelu:</label>
                <select
                  id="horseSortOrder"
                  v-model="sortOrder"
                  class="custom-select w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-800 dark:text-white transition duration-150 ease-in-out"
                  style="-webkit-appearance: none; -moz-appearance: none; appearance: none;"
                >
                  <option value="name">Nimi (A-Ö)</option>
                  <option value="price_desc">Hinta (Kallein)</option>
                  <option value="price_asc">Hinta (Halvin)</option>
                </select>
              </div>
            </div>
            
            <!-- Racing status filter buttons -->
            <div class="mt-4">
              <label class="block text-sm font-medium mb-2">Näytä hevoset:</label>
              <div class="flex flex-wrap gap-2">
                <button 
                  @click="racingFilter = 'all'" 
                  class="filter-btn px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150"
                  :class="racingFilter === 'all' ? 'filter-btn-active' : 'filter-btn-inactive'"
                >
                  Kaikki
                </button>
                <button 
                  @click="racingFilter = 'today'" 
                  class="filter-btn px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 racing-today-btn"
                  :class="racingFilter === 'today' ? 'filter-btn-active' : 'filter-btn-inactive'"
                >
                  Tänään
                </button>
                <button 
                  @click="racingFilter = 'listed'" 
                  class="filter-btn px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 racing-soon-btn"
                  :class="racingFilter === 'listed' ? 'filter-btn-active' : 'filter-btn-inactive'"
                >
                  Lähtölistoilla
                </button>
                <button 
                  @click="racingFilter = 'any'" 
                  class="filter-btn px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 racing-any-btn"
                  :class="racingFilter === 'any' ? 'filter-btn-active' : 'filter-btn-inactive'"
                >
                  Tänään tai listalla
                </button>
              </div>
            </div>
          </div>
  
          <div class="horse-select-header grid grid-cols-12 gap-x-3 font-semibold text-sm text-gray-600 dark:text-gray-400 pb-2 mb-1 pr-2">
            <span class="col-span-6">Nimi</span>
            <span class="col-span-3 text-right">Hinta</span>
            <span class="col-span-3"></span> 
          </div>
          <div class="horse-select-header-underline"></div>
          <ul class="modal-list max-h-72 overflow-y-auto space-y-1 styled-scrollbar pr-2">
            <li v-if="loading" class="text-center py-4 text-gray-500 dark:text-gray-400">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
              Ladataan hevosia...
            </li>
            <li v-else-if="filteredAndSortedHorses.length === 0 && !loading" class="text-center py-4 text-gray-500 dark:text-gray-400">
              Ei hevosia valittu.
            </li>
            <li
              v-for="horse in filteredAndSortedHorses"
              :key="horse.id"
              class="horse-select-item items-center border-b border-gray-100 dark:border-gray-700 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150 ease-in-out" 
              :class="{ 
                'opacity-60 cursor-not-allowed': isHorseDisabled(horse.id, horse.price),
                'racing-today': horse.racing === 'today',
                'racing-soon': horse.racing === 'yes',
                'price-too-high': horse.price > (playerMoney + changingHorsePrice)
              }"
            >
              <span
                class="horse-select-name"
                @click="!isHorseDisabled(horse.id, horse.price) ? viewHorsePerformance(horse.id, horse.name) : null"
                :title="isHorseDisabled(horse.id, horse.price) ? (horse.price > (playerMoney + changingHorsePrice) ? 'Ei tarpeeksi rahaa' : 'Hevonen jo valittu') : `Näytä ${horse.name} suoritukset`"
              >
                <span>{{ horse.name }}</span>
                <small v-if="formatUpcomingStarts(horse)" class="horse-starts">
                  {{ formatUpcomingStarts(horse) }}
                </small>
              </span>
              <span class="horse-select-price text-right text-gray-600 dark:text-gray-300">{{ horse.price.toLocaleString() }}</span>
              <div class="horse-select-action justify-self-end">
                <button
                  @click="selectHorse(horse)"
                  class="horse-select-btn"
                  :disabled="isHorseDisabled(horse.id, horse.price)"
                  :title="isHorseDisabled(horse.id, horse.price) ? (horse.price > (playerMoney + changingHorsePrice) ? 'Ei tarpeeksi rahaa' : 'Hevonen jo valittu') : `Valitse ${horse.name}`"
                >
                  Valitse
                </button>
              </div>
            </li>
          </ul>
          <div
            v-if="!loading && filteredAndSortedHorses.length > 0"
            class="horse-list-summary"
          >
            <div>Näytetään {{ filteredAndSortedHorses.length }} hevosta.</div>
            <div class="racing-legend mt-2">
              <div class="racing-legend-item">
                <span class="racing-legend-color racing-today-color"></span>
                <span class="racing-legend-text">Kilpailee tänään</span>
              </div>
              <div class="racing-legend-item">
                <span class="racing-legend-color racing-soon-color"></span>
                <span class="racing-legend-text">Löytyy lähtölistoilta</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  
    <Teleport to="body">
      <HorsePerformanceModal
          :show="isHorsePerformanceModalVisible"
          :horse-id="selectedHorseIdForPerformance"
          :horse-name="selectedHorseNameForPerformance"
          @close="closeHorsePerformanceModal"
      />
    </Teleport>
  </template>
  
  <script>
  // Script remains the same as in HorseSelectionModalVueUniqueClasses
  import { ref, watch, computed } from 'vue';
  import axios from '../plugins/axios';
  import HorsePerformanceModal from './HorsePerformanceModal.vue';
  
  export default {
    name: 'HorseSelectionModal',
    components: { HorsePerformanceModal },
    props: {
      show: Boolean,
      currentlySelectedHorseIds: {
        type: Array,
        default: () => []
      },
      changingHorseId: {
        type: [String, Number, null],
        default: null
      },
      playerMoney: {
        type: Number,
        default: 0
      },
      changingHorsePrice: {
        type: Number,
        default: 0
      }
    },
    emits: ['close', 'horse-selected'],
    setup(props, { emit }) {
      const allHorses = ref([]);
      const sortOrder = ref('price_desc'); // Default to price from high to low
      const searchTerm = ref('');
      const loading = ref(false);
      const racingFilter = ref('all'); // 'all', 'today', 'listed', or 'any'
  
      const isHorsePerformanceModalVisible = ref(false);
      const selectedHorseIdForPerformance = ref(null);
      const selectedHorseNameForPerformance = ref('');
  
      const fetchHorses = async () => {
        if (!props.show) return;
        loading.value = true;
        try {
          const publicBase = (process.env.VUE_APP_PUBLIC_DATA_BASE_URL || '').trim();
          const publicUrl = publicBase
            ? `${publicBase.replace(/\/$/, '')}/horses.json`
            : '';

          // Prefer CloudFront/S3-served catalog (cheap + fast). Fall back to API if missing/bad.
          if (publicUrl) {
            try {
              const resp = await fetch(publicUrl, { method: 'GET' });
              if (!resp.ok) {
                throw new Error(`HTTP ${resp.status}`);
              }
              const data = await resp.json();
              if (!Array.isArray(data)) {
                throw new Error('Unexpected JSON shape (expected array)');
              }
              const mapped = data
                .filter(item => item && item.horse_id && item.horse_name)
                .map(item => ({
                  id: item.horse_id,
                  name: item.horse_name,
                  price: parseFloat(item.price) || 0,
                  racing: item.racing || "no",
                  upcomingStarts: Array.isArray(item.upcomingStarts) ? item.upcomingStarts : []
                }));

              // Guard: CloudFront SPA error mapping can return index.html (200) if the file is missing.
              if (mapped.length > 0) {
                allHorses.value = mapped;
                return;
              }
              throw new Error('Public horses catalog empty/unusable');
            } catch (e) {
              console.warn('Public horses catalog fetch failed, falling back to API:', e);
            }
          }

          const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/horses`);
          allHorses.value = response.data.map(item => ({
            id: item.horse_id,
            name: item.horse_name,
            price: parseFloat(item.price) || 0,
            racing: item.racing || "no", // Add racing status
            upcomingStarts: Array.isArray(item.upcomingStarts) ? item.upcomingStarts : []
          }));
        } catch (error) {
          console.error('Error fetching horses for modal:', error.response?.data || error.message);
          allHorses.value = []; 
        } finally {
          loading.value = false;
        }
      };
  
      const filteredAndSortedHorses = computed(() => {
        let horsesToDisplay = [...allHorses.value];
  
        // Apply search filter
        if (searchTerm.value.trim() !== '') {
          horsesToDisplay = horsesToDisplay.filter(horse =>
            horse.name.toLowerCase().includes(searchTerm.value.toLowerCase())
          );
        }
        
        // Apply racing status filter
        if (racingFilter.value !== 'all') {
          horsesToDisplay = horsesToDisplay.filter(horse => {
            if (racingFilter.value === 'today') {
              return horse.racing === 'today';
            } else if (racingFilter.value === 'listed') {
              return horse.racing === 'yes';
            } else if (racingFilter.value === 'any') {
              return horse.racing === 'today' || horse.racing === 'yes';
            }
            return true;
          });
        }
  
        // Apply sorting
        horsesToDisplay.sort((a, b) => {
          if (sortOrder.value === 'name') {
            return (a.name || '').localeCompare(b.name || '');
          } else if (sortOrder.value === 'price_desc') {
            return (b.price || 0) - (a.price || 0);
          } else if (sortOrder.value === 'price_asc') {
            return (a.price || 0) - (b.price || 0);
          }
          return 0;
        });
        return horsesToDisplay;
      });
  
      const selectHorse = (horse) => {
        if (isHorseDisabled(horse.id, horse.price)) {
          if (horse.price > (props.playerMoney + props.changingHorsePrice)) {
            alert('Ei tarpeeksi rahaa tämän hevosen valitsemiseen.');
          }
          return;
        }
        emit('horse-selected', horse);
      };
  
      const closeModal = () => {
        emit('close');
      };
  
      const isHorseDisabled = (horseId, horsePrice) => {
        // Check if horse is already selected (except the one being changed)
        const isAlreadySelected = props.currentlySelectedHorseIds.includes(horseId) && 
                                 horseId !== props.changingHorseId;
        
        // Check if player can afford this horse
        const canAfford = horsePrice <= (props.playerMoney + props.changingHorsePrice);
        
        return isAlreadySelected || !canAfford;
      };
  
      const viewHorsePerformance = (horseId, horseName) => {
        selectedHorseIdForPerformance.value = horseId;
        selectedHorseNameForPerformance.value = horseName;
        isHorsePerformanceModalVisible.value = true;
      };

      const formatUpcomingStarts = (horse) => {
        const starts = Array.isArray(horse?.upcomingStarts) ? horse.upcomingStarts : [];
        if (starts.length === 0) {
          return '';
        }
        return starts
          .map(start => {
            const date = start.displayDate || start.date || '';
            const venue = start.venue || '';
            const raceNumber = start.raceNumber ? `/${start.raceNumber}` : '';
            return [date, `${venue}${raceNumber}`.trim()].filter(Boolean).join(' ');
          })
          .filter(Boolean)
          .join(', ');
      };
  
      const closeHorsePerformanceModal = () => {
        isHorsePerformanceModalVisible.value = false;
        selectedHorseIdForPerformance.value = null;
        selectedHorseNameForPerformance.value = '';
      };
  
      watch(() => props.show, (newVal, oldVal) => {
        if (newVal && !oldVal) {
          searchTerm.value = ''; 
          racingFilter.value = 'all'; // Reset filter when modal opens
          if (allHorses.value.length === 0) { 
              fetchHorses();
          }
        }
      });
  
      return {
        allHorses,
        sortOrder,
        searchTerm,
        loading,
        racingFilter,
        closeModal,
        selectHorse,
        isHorseDisabled,
        viewHorsePerformance,
        formatUpcomingStarts,
        closeHorsePerformanceModal,
        isHorsePerformanceModalVisible,
        selectedHorseIdForPerformance,
        selectedHorseNameForPerformance,
        filteredAndSortedHorses,
      };
    }
  };
  </script>
  
  <style scoped>
  /* Styles for the custom scrollbar */
  .styled-scrollbar::-webkit-scrollbar {
    width: 8px;
  }
  .styled-scrollbar::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 10px;
  }
  .styled-scrollbar::-webkit-scrollbar-thumb {
    background: #a0aec0; 
  }
  .dark .styled-scrollbar::-webkit-scrollbar-thumb {
    background: #4a5568; 
  }
  .styled-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #718096; 
  }
  .dark .styled-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #2d3748; 
  }
  .styled-scrollbar {
    scrollbar-width: thin; 
    scrollbar-color: #a0aec0 transparent; 
  }
  .dark .styled-scrollbar {
    scrollbar-color: #4a5568 transparent; 
  }
  
  /* Racing status styles */
  .racing-today {
    background-color: rgba(52, 211, 153, 0.15); /* Light green background */
  }
  .dark .racing-today {
    background-color: rgba(52, 211, 153, 0.1);
  }
  .racing-soon {
    background-color: rgba(96, 165, 250, 0.15); /* Light blue background */
  }
  .dark .racing-soon {
    background-color: rgba(96, 165, 250, 0.1);
  }
  
  /* Racing legend styles */
  .racing-legend {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1rem;
    margin-top: 0.5rem;
    font-size: 0.85rem;
  }
  .racing-legend-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .racing-legend-color {
    display: inline-block;
    width: 1rem;
    height: 1rem;
    border-radius: 0.25rem;
  }
  .racing-today-color {
    background-color: rgba(52, 211, 153, 0.5);
  }
  .racing-soon-color {
    background-color: rgba(96, 165, 250, 0.5);
  }
  .racing-legend-text {
    color: #6b7280;
  }
  .dark .racing-legend-text {
    color: #9ca3af;
  }
  
  /* --- CRITICAL ADDITION FOR LAYOUT --- */
  
  /* --- CRITICAL ADDITION FOR LAYOUT --- */
  /* Force grid display for horse list items and define column template */
  .horse-select-item {
    display: grid !important; /* Force grid display */
    grid-template-columns: repeat(12, minmax(0, 1fr)) !important; /* Replicate Tailwind's grid-cols-12 */
    gap: 0.75rem !important; /* Replicate Tailwind's gap-x-3 (0.75rem = 12px if base font is 16px) */
    /* Tailwind's `items-center` is already on the class list, should work if display:grid is active */
  }
  
  /* Assign grid column spans using direct child selectors for robustness */
  /* These will apply after Tailwind's col-span utilities if there's a conflict, */
  /* or ensure the layout if Tailwind's col-span isn't working. */
  .horse-select-item > .horse-select-name {
    grid-column: span 6 / span 6 !important;
  }
  .horse-select-item > .horse-select-price {
    grid-column: span 3 / span 3 !important;
  }
  .horse-select-item > .horse-select-action {
    grid-column: span 3 / span 3 !important;
  }
  
  /* Ensure the header also respects this if Tailwind fails for it */
  .horse-select-header {
    display: grid !important;
    grid-template-columns: repeat(12, minmax(0, 1fr)) !important;
    gap: 0.75rem !important;
  }
  .horse-select-header > span:nth-child(1) {
    grid-column: span 6 / span 6 !important;
  }
  .horse-select-header > span:nth-child(2) {
    grid-column: span 3 / span 3 !important;
  }
  .horse-select-header > span:nth-child(3) {
    grid-column: span 3 / span 3 !important;
  }
  
  .horse-select-btn {
    background-color: #64ffda;
    color: #0a192f;
    padding: 8px 18px;
    border: none;
    border-radius: 5px;
    font-size: 0.95rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s, color 0.2s, box-shadow 0.2s;
    box-shadow: 0 2px 6px rgba(100,255,218,0.08);
    outline: none;
    margin-top: 4px;
    margin-bottom: 4px;
    /* Add vertical margin for separation */
  }
  .horse-select-btn:hover:not(:disabled) {
    background-color: #52d6c0;
    color: #0a192f;
    box-shadow: 0 4px 12px rgba(100,255,218,0.18);
  }
  .horse-select-btn:disabled,
  .horse-select-btn[disabled] {
    background-color: #b2f5ea;
    color: #7b8fa3;
    cursor: not-allowed;
    opacity: 0.7;
    box-shadow: none;
  }

  .horse-select-name {
    color: #38bdf8; /* Turquoise/blue accent */
    cursor: pointer;
    text-decoration: none;
    transition: color 0.18s, text-decoration 0.18s;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }
  .horse-select-name:hover,
  .horse-select-name:focus {
    color: #0ea5e9; /* Slightly darker turquoise on hover */
    text-decoration: underline;
  }
  .horse-starts {
    color: #94a3b8;
    font-size: 0.72rem;
    line-height: 1.15;
    text-decoration: none;
  }
  .horse-select-item.opacity-60 .horse-select-name,
  .horse-select-item.cursor-not-allowed .horse-select-name {
    color: #7b8fa3;
    cursor: not-allowed;
    text-decoration: none;
    pointer-events: none;
  }

  /* Money available info styling */
  .money-available-info {
    background-color: rgba(20, 184, 166, 0.1);
    border: 1px solid rgba(20, 184, 166, 0.3);
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 16px;
  }
  
  /* Price too high styling */
  .price-too-high {
    background-color: rgba(239, 68, 68, 0.1) !important;
    border-left: 3px solid rgba(239, 68, 68, 0.5) !important;
  }
  
  /* Add more visual separation before the summary text */
  .horse-list-summary {
    margin-top: 2.5rem;      /* More vertical gap from the horse list */
    padding-top: 1.2rem;     /* Padding above the border */
    border-top: 2.5px solid #e5e7eb;
    text-align: center;
    font-size: 0.95rem;
    color: #6b7280;
    background: none;
  }
  @media (prefers-color-scheme: dark) {
    .horse-list-summary {
      border-top: 2.5px solid #374151;
      color: #9ca3af;
    }
  }

  .horse-select-header-underline {
    border-bottom: 2.5px solid #e5e7eb;
    margin-bottom: 0.5rem;
    margin-top: -0.2rem;
  }
  @media (prefers-color-scheme: dark) {
    .horse-select-header-underline {
      border-bottom: 2.5px solid #374151;
    }
  }

  /* Responsive improvement for controls-grid */
  .controls-grid .flex-col {
    flex-direction: column;
  }
  @media (min-width: 768px) {
    .controls-grid .flex-col {
      flex-direction: row;
    }
    .controls-grid .flex-1 {
      min-width: 0;
    }
    .controls-grid .mt-4 {
      margin-top: 0 !important;
    }
  }

  /* Custom form elements styling */
  .custom-input,
  .custom-select {
    background-color: #1e3a5f;
    border: 1px solid rgba(100, 255, 218, 0.2);
    color: #e6f1ff;
    transition: all 0.3s ease;
  }
  
  .custom-input:hover,
  .custom-select:hover {
    border-color: rgba(100, 255, 218, 0.4);
    box-shadow: 0 0 0 1px rgba(100, 255, 218, 0.1);
  }
  
  .custom-input:focus,
  .custom-select:focus {
    border-color: #64ffda;
    box-shadow: 0 0 0 2px rgba(100, 255, 218, 0.2);
    outline: none;
  }
  
  .custom-input::placeholder {
    color: rgba(230, 241, 255, 0.5);
  }
  
  /* Custom select styling with dropdown arrow */
  .custom-select {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2364ffda' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
    background-position: right 0.5rem center;
    background-repeat: no-repeat;
    background-size: 1.5em 1.5em;
    padding-right: 2.5rem;
  }
  
  /* Ensure controls-row is flex and responsive */
  .controls-row {
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  @media (min-width: 768px) {
    .controls-row {
      flex-direction: row;
      gap: 1.5rem;
    }
    .controls-row .flex-1 {
      min-width: 0;
    }
    .controls-row .mt-4 {
      margin-top: 0 !important;
    }
  }

  /* Form controls container styling */
  .form-controls-container {
    background-color: #112240;
    border: 1px solid #1e3a5f;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  /* Label styling */
  .form-controls-container label {
    color: #64ffda;
    font-weight: 500;
    margin-bottom: 0.5rem;
    display: block;
    font-size: 0.875rem;
  }
  
  /* Filter button styles */
  .filter-btn {
    border: 1px solid;
    min-width: 100px;
    transition: all 0.2s ease;
    cursor: pointer;
  }
  
  .filter-btn-active {
    background-color: #64ffda;
    color: #0a192f;
    border-color: #64ffda;
    box-shadow: 0 2px 6px rgba(100,255,218,0.2);
  }
  
  .filter-btn-inactive {
    background-color: transparent;
    color: #e6f1ff;
    border-color: rgba(100, 255, 218, 0.3);
  }
  
  .filter-btn-inactive:hover {
    border-color: #64ffda;
    box-shadow: 0 1px 3px rgba(100,255,218,0.1);
  }
  
  /* Racing status button indicators */
  .racing-today-btn.filter-btn-active {
    background-color: rgba(52, 211, 153, 0.8);
  }
  
  .racing-soon-btn.filter-btn-active {
    background-color: rgba(96, 165, 250, 0.8);
  }
  
  .racing-any-btn.filter-btn-active {
    background: linear-gradient(135deg, rgba(52, 211, 153, 0.8) 0%, rgba(96, 165, 250, 0.8) 100%);
  }
  
  /* Remove conflicting grid from controls-grid on md+ screens */
  @media (min-width: 768px) {
    .controls-grid {
      display: block !important;
      grid-template-columns: none !important;
      gap: 0 !important;
    }
  }

  .horse-select-title {
    color: #64ffda; /* Turquoise accent, matches your app's accent color */
    /* Optionally add text-shadow for better contrast on dark backgrounds */
    text-shadow: 0 1px 8px rgba(10,25,47,0.18);
    letter-spacing: 0.5px;
  }
  
  </style>
